// Armazón de Pulso, portado 1:1 desde `AppInner` (src/App.tsx), reemplazando el estado local
// `activeView`/`localStorage` por la URL real (`useLocation`/`useNavigate`). El sidebar navega
// vía `NavLink` (T-6) — ya no recibe `activeView` como prop.
//
// Ver docs/superpowers/specs/2026-08-04-menu-unificado-design.md, Fase 1.

import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdminPanelProvider } from '@/contexts/AdminPanelContext';
import { UnsavedChangesProvider } from '@/contexts/UnsavedChangesContext';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useVisibleNavLinks } from '@/hooks/useVisibleNavLinks';
import { usePlatform } from '@/hooks/usePlatform';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import TeamsSDK from '@/teams/TeamsSDK';
import { GradientBackground } from '@/components/layout/GradientBackground';
import { Sidebar } from '@/components/layout/Sidebar';
import { MobileNav } from '@/components/layout/MobileNav';
import { UserConfigFAB } from '@/components/common/UserConfigFAB';
import { DemoSimulatorPanel } from '@/demo/DemoSimulatorPanel';
import { DebugPanel } from '@/debug/DebugPanel';
import { IS_DEMO_MODE, IS_DEV_BUILD } from '@/config/featureFlags';
import { resolveView, VIEW_PATHS } from './pulsoRoutes';
import { withBase } from '@/utils/basePath';

const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'Sigma Pulse';

function LoadingScreen() {
  return (
    <div className="app-loading">
      <div className="app-loading-spinner" />
      <span>{APP_NAME}</span>
    </div>
  );
}

/** Ningún módulo habilitado: no hay menú al que redirigir, así que la pantalla lo dice. */
function NoAccessScreen() {
  const { t } = useTranslation();
  return (
    <div className="app-no-access">
      <i className="fas fa-lock" aria-hidden="true" />
      <h1>{t('access.noModulesTitle')}</h1>
      <p>{t('access.noModulesDesc')}</p>
    </div>
  );
}

export function PulsoLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { platform, teamsContext, isReady } = usePlatform();
  const { setFromTeams } = useThemeContext();
  const { links: visibleNavLinks, isViewVisible, modules, profileResolved } = useVisibleNavLinks();

  // Resuelto por prefijo (I-2, revisión final): un match exacto contra `PATH_TO_VIEW` no cubre la
  // ruta hermana `/admin/:section` (deep-links de Fundamentales, Task 11) — caía siempre al
  // fallback 'survey' y rebotaba deep-links válidos para roles sin módulo Pulso.
  const activeView = resolveView(location.pathname);
  // Primera entrada accesible del menú: destino cuando la vista activa deja de estarlo. No se fija
  // en 'survey': pertenece a Pulso, y un usuario sin ese módulo quedaría redirigido a una vista a
  // la que tampoco tiene derecho.
  const fallbackView = visibleNavLinks[0]?.view;

  // Un usuario SIN módulo Pulso que aterriza en la raíz ('/', vista `survey`) —el destino por
  // defecto tras el login— no debe caer en el fallback universal de Fundamentales solo porque esa
  // vista es visible para cualquiera: su lugar es Reconocimiento. Cruce de página completa (deja el
  // armazón de Pulso), simétrico al `goToPulsoPortal` de LandingDecision.tsx. Se limita a la raíz
  // (no a cualquier ruta de Pulso sin acceso) para no romper deep-links deliberados a `/admin` o
  // `/fundamentales`, que SÍ siguen siendo válidos para un admin de Brillamos sin módulo Pulso.
  const goToBrillamosPortal = profileResolved && activeView === 'survey'
    && !modules.canViewPulso && modules.canViewBrillamos;
  useEffect(() => {
    if (goToBrillamosPortal) window.location.replace(withBase('/app/perfil'));
  }, [goToBrillamosPortal]);

  // Teams theme sync — independiente del idioma, corre siempre que haya contexto de Teams.
  useEffect(() => {
    if (teamsContext) {
      setFromTeams(teamsContext.theme);
      TeamsSDK.registerOnThemeChangeHandler(setFromTeams);
    }
  }, [teamsContext, setFromTeams]);

  // Idioma: misma lógica que ya usa AppLayout (Reconocimiento) — demoUser.idioma trae la
  // precedencia completa resuelta en UserContext.
  useAppLanguage();

  // Si la vista activa deja de ser accesible (cambio de rol/permisos/módulos, o una URL escrita a
  // mano sin acceso), se redirige a la primera entrada visible del menú.
  useEffect(() => {
    if (profileResolved && fallbackView && activeView !== fallbackView && !isViewVisible(activeView)) {
      navigate(VIEW_PATHS[fallbackView], { replace: true });
    }
  }, [profileResolved, isViewVisible, activeView, fallbackView, navigate]);

  if (!isReady) return <LoadingScreen />;
  // No decidir acceso ni pintar el menú mientras el perfil autenticado aún resuelve: durante la
  // ventana de carga los roles caen a su fallback → `visibleNavLinks` queda vacío → `fallbackView`
  // undefined → NoAccessScreen/redirect, y el menú (incluida Home) parpadea apareciendo y
  // desapareciendo. Mismo criterio y fix que /app/admin (commit f6df2f4). En local/demo
  // `profileResolved` es `true` desde el primer render → no añade ningún loader.
  if (!profileResolved) return <LoadingScreen />;
  if (goToBrillamosPortal) return <LoadingScreen />;
  if (!fallbackView) return <NoAccessScreen />;

  // Guard en RENDER, no solo en efecto (I-1, revisión final): el `useEffect` de arriba corre
  // DESPUÉS del commit, y los efectos de los hijos corren ANTES que los del padre — un `<Outlet />`
  // incondicional montaba la vista destino (y disparaba sus queries al backend, p. ej.
  // `useAdminUsers()` en `/usuarios`) antes de que el efecto del padre alcanzara a redirigir. "Una
  // vista sin acceso no se monta" es invariante del proyecto (ver CLAUDE.md, sección de reglas de
  // acceso) — el `useEffect` de arriba se conserva solo para el caso de que los permisos cambien EN
  // CALIENTE mientras el usuario ya está en la vista (cambio de rol vía el simulador de demo).
  if (!isViewVisible(activeView)) {
    return <Navigate to={VIEW_PATHS[fallbackView]} replace />;
  }

  return (
    <AdminPanelProvider>
    <UnsavedChangesProvider>
      <GradientBackground />

      <Sidebar />

      <main className="main-content">
        <div className="view">
          <Outlet />
        </div>
      </main>

      <MobileNav />

      {IS_DEV_BUILD && <UserConfigFAB />}
      {IS_DEMO_MODE && <DemoSimulatorPanel />}
      <DebugPanel />

      {platform === 'teams' && (
        <div className="teams-badge">{t('platform.teams')}</div>
      )}
    </UnsavedChangesProvider>
    </AdminPanelProvider>
  );
}

export default PulsoLayout;
